/**
 * TextItemsList - Component for displaying text items in a clean list format
 */

import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { TextSnippet as TextIcon } from "@mui/icons-material";

export default function TextItemsList({
  items = [],
  maxItems = 20,
  showTimestamps = true,
  compact = false,
}) {
  const displayItems = items.slice(0, maxItems);

  if (!items || items.length === 0) {
    return (
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          No text items
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <TextIcon color="action" />
        <Typography
          variant="subtitle2"
          color="primary"
          sx={{ fontWeight: 600 }}
        >
          Text Items ({items.length})
        </Typography>
      </Stack>

      <Stack spacing={compact ? 1 : 1.5}>
        {displayItems.map((item, index) => (
          <Card
            key={item.id || index}
            variant="outlined"
            sx={{
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 1,
                transform: "translateY(-1px)",
              },
            }}
          >
            <CardContent sx={{ p: compact ? 1.5 : 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={`#${index + 1}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                  <Chip
                    label="text"
                    size="small"
                    color="info"
                    sx={{ fontSize: "0.7rem", height: 20 }}
                  />
                </Box>
                {showTimestamps && item.timestamp && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.timestamp).toLocaleString()}
                  </Typography>
                )}
              </Box>

              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.6,
                  color: "text.primary",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {item.content || item.description || "No content"}
              </Typography>

              {item.error && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="error">
                    Error: {item.error}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
